import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Lock } from 'lucide-react';
import {
  addEvaluationTemplateVersionMutation,
  getEvaluationTemplateOptions,
  getEvaluationTemplateQueryKey,
  listEvaluationTemplatesQueryKey,
  publishEvaluationTemplateVersionMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, Card, InlineConfirm, Skeleton, useToast } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import type { EvaluationTemplateVersion } from '../../../services/api/evaluation-template.api.js';
import { SpecComposer } from './SpecComposer.js';
import type { SpecItem } from './SpecComposer.js';

interface VersionPanelProps {
  templateId: number;
  subjectKind: string;
}

/**
 * A version's lifecycle is `draft` | `locked` now (ENTITY-MODEL-PLAN.md §2.1): publishing locks it,
 * and a locked version is immutable — editing means a new draft. The old four-state `status`
 * column (draft/active/deprecated/retired) is gone; "which one is in force" is the template's
 * `currentVersionId` pointer, not a per-row flag.
 */
const STATE_VARIANTS: Record<string, 'success' | 'warning' | 'neutral'> = {
  locked: 'success',
  draft: 'warning',
};

const STATE_ICONS: Record<string, React.ReactNode> = {
  locked: <Lock size={13} className="text-success" />,
  draft: <Clock size={13} className="text-warning" />,
};

function buildSpec(subjectKind: string, items: SpecItem[]): Record<string, unknown> {
  return {
    schemaVersion: 'evaluation-template-spec/v1',
    subjectKind,
    items,
  };
}

export const VersionPanel: React.FC<VersionPanelProps> = ({ templateId, subjectKind }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  // `window.confirm` blocks the event loop (the browser suite cannot drive past it) and cannot
  // be styled; the confirmation is asked in place instead.
  const [confirmPublishId, setConfirmPublishId] = useState<number | null>(null);
  const [addingVersion, setAddingVersion] = useState(false);
  const [newSpecItems, setNewSpecItems] = useState<SpecItem[]>([]);

  const {
    data: templateData,
    isLoading,
    isError,
  } = useQuery(getEvaluationTemplateOptions({ path: { id: templateId } }));
  const template = templateData?.data;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getEvaluationTemplateQueryKey({ path: { id: templateId } }) }),
      queryClient.invalidateQueries({ queryKey: listEvaluationTemplatesQueryKey() }),
    ]);

  const addVersionMutation = useMutation({
    ...addEvaluationTemplateVersionMutation(),
    onSuccess: async () => {
      notify('Draft version saved');
      await refresh();
      setAddingVersion(false);
      setNewSpecItems([]);
    },
  });

  const publishMutation = useMutation({
    ...publishEvaluationTemplateVersionMutation(),
    onSuccess: async () => {
      notify('Version published');
      await refresh();
    },
    onError: () => notify('Could not publish the version', 'danger'),
  });
  const publishingId = publishMutation.isPending ? publishMutation.variables?.path.versionId : undefined;

  if (isLoading) return <Skeleton className="h-24 rounded-lg" />;
  if (isError) return <Callout tone="danger">Could not load the versions.</Callout>;

  const versions: EvaluationTemplateVersion[] = template?.versions ?? [];
  const currentVersionId = template?.currentVersionId ?? null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-heading">Versions</p>
        {!addingVersion && (
          <Can policy="CanManageEvaluationTemplates">
            <Button size="sm" variant="ghost" onClick={() => setAddingVersion(true)}>
              Add version
            </Button>
          </Can>
        )}
      </div>

      {versions.length === 0 && !addingVersion && <p className="text-xs text-faint">No versions yet.</p>}

      {versions.map((v) => (
        <Card key={v.id}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {STATE_ICONS[v.state] ?? null}
              <span className="text-sm font-medium text-heading">v{v.version}</span>
              <Badge variant={STATE_VARIANTS[v.state] ?? 'neutral'}>{v.state}</Badge>
              {v.id === currentVersionId && <Badge variant="success">in force</Badge>}
            </div>
            {v.state === 'draft' && (
              <Can policy="CanManageEvaluationTemplates">
                {confirmPublishId === v.id ? (
                  <InlineConfirm
                    message={`Publish v${v.version}? This makes it active and immutable.`}
                    confirmLabel="Publish"
                    confirmVariant="warning"
                    loading={publishingId === v.id}
                    onConfirm={() => {
                      publishMutation.mutate({ path: { id: templateId, versionId: v.id } });
                      setConfirmPublishId(null);
                    }}
                    onCancel={() => setConfirmPublishId(null)}
                  />
                ) : (
                  <Button
                    size="sm"
                    variant="success"
                    loading={publishingId === v.id}
                    onClick={() => setConfirmPublishId(v.id)}
                  >
                    <CheckCircle2 size={12} /> Publish
                  </Button>
                )}
              </Can>
            )}
          </div>
          <p className="mt-1 text-xs text-muted">Created {new Date(v.createdAt).toLocaleString()}</p>
        </Card>
      ))}

      {addingVersion && (
        <Card title="New draft version">
          <div className="space-y-3">
            <SpecComposer subjectKind={subjectKind} items={newSpecItems} onChange={setNewSpecItems} />
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                loading={addVersionMutation.isPending}
                onClick={() =>
                  addVersionMutation.mutate({
                    path: { id: templateId },
                    body: { spec: buildSpec(subjectKind, newSpecItems) },
                  })
                }
                disabled={newSpecItems.length === 0}
              >
                Save draft
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAddingVersion(false);
                  setNewSpecItems([]);
                }}
              >
                Cancel
              </Button>
            </div>
            {addVersionMutation.isError && (
              <Callout tone="danger">Failed to create the version. Check the spec and try again.</Callout>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
